// SNOWCLUB-OPTIMIZE-SSR-PAINT-CORR-20260930-003 — THE PARSER-FIRST PAINT REVEAL, for ssr=true DesignSurface hosts.
//
// THE MEASURED DEFECT. DesignSurface.tsx's own reveal (`host.style.visibility = "visible"`) lives
// inside a React `useEffect` — and a React effect cannot fire until the component's tree has hydrated,
// which needs the FULL page JS bundle downloaded, parsed and executed. Traced with a real Playwright
// Chromium load against a production standalone build, CPU 4x + Slow-4G (tooling/port-harness/
// paint-gate-trace.mjs, /mountain-whistler/profile): `window.__SG_DESIGN_STATE` stayed `undefined`
// until 13.7s — DesignSurface's effect had not run AT ALL — and the page only appeared because
// ServerLayerParserFirst's own "no-boot-by-ceiling" escape hatch (built for a genuinely dead boot,
// SERVER_LAYER_CEILING_MS=12000) removed the `data-sg-js` stamp and let the no-JS CSS rule paint the
// (already correct, already server-rendered) content. That is not a failure path firing on a failure —
// it is the ONLY path a slow-but-completely-healthy mobile load had, because nothing else in the
// document was in a position to say "this is already right" sooner. Every actual asset was fast
// (the three boot-dependency scripts finished in 282–698ms in the same trace); only the decision to
// reveal was gated behind something unrelated to whether the pixels were correct.
//
// THE FIX. A plain, parser-first inline script — the exact idiom ServerLayerParserFirst.tsx already
// uses for the analogous problem on the OTHER layer — that does not wait for React at all. It runs the
// instant its own host div has been parsed (`document.currentScript.previousElementSibling`), and for
// an ssr=true surface the "painted" check is already true at that instant: react-dom/server put the
// real `[data-dc-tpl]` template nodes in the response, they are not built by hydration. It loads the
// SAME three boot-dependency classic scripts DesignSurface.tsx's own effect would, in the same order,
// under the same `data-sg-design` marker its loader already checks — so whichever path (this one or
// the React effect) gets there first, the other sees them already on the page and never fetches twice.
// ⚠ IMPL-004: SHARING THE TAG IS THE POINT AND WAS ALSO THE BUG. DesignSurface's loader used to treat
// a found tag as LOADED, so a tag this script had inserted and was still fetching (or had already
// 404'd) let the React effect publish `__SG_DESIGN_STATE = "up"` over a design whose custom elements
// were never defined. Both writers now share ONE readiness contract — design-script-readiness.ts —
// and this script's `loadOne` and that module's `loadDesignScript` resolve only from a stamped
// completion marker or the tag's own load/error event. The reveal then —
// waits, BOUNDED, for `document.fonts.ready`, and only then reveals: `visibility:visible`, plus the
// same `__SG_DESIGN_STATE` / dataset bookkeeping the React effect writes on a genuine "up". When
// React's effect DOES eventually run (once hydration completes) it reads `designState === "up" &&
// designPresent` and takes its OWN pre-existing short-circuit (DesignSurface.tsx, the boot effect's
// very first guard) — nothing in that effect changed; this only wins the same race sooner.
//
// WHY THE FONT WAIT, BOUNDED RATHER THAN OMITTED. The design's webfonts are `font-display:swap`
// (apps/root/public/design/_ds/*/tokens/fonts.css) — correct per web.dev's own LCP guidance, and NOT
// touched here. But `swap` means the fallback system font is what paints the instant text lays out; if
// this script revealed the surface before the brand faces had even started downloading, the visitor
// would see a fallback-font flash they do not see today (today the ~12s wait is, incidentally, long
// enough for fonts to finish). `document.fonts.ready` bounded at FONT_CEILING_MS is the same swap
// contract, just no longer serialised behind the whole JS bundle: it waits for the ACTUAL dependency
// (glyphs), not a proxy for it (hydration), and gives up after a bound so a slow/failed font fetch
// cannot re-introduce the same class of indefinite hide this file exists to remove.
//
// WHY THIS CANNOT DESYNC FROM WHAT HYDRATION LATER RENDERS. design-runtime-bundle.mjs's own
// "INITIAL-RENDER RULE" (see its header comment) already guarantees the server render and the client's
// FIRST hydrating render agree exactly — same AST, same env, same `__afterHydration=false` on both
// first passes. Hydration reconciling against matching markup does not touch the DOM; it only attaches
// event handlers. Revealing before it completes changes nothing about which pixels are on screen, only
// how soon they appear. Anything the design mounts LATER than first paint (menus, `__afterHydration`
// content) is off-screen by construction (display:none/opacity:0) whether this script has run or not.
//
// FAILS CLOSED, ALWAYS. A boot-dependency 404, a fonts API that never resolves past its own ceiling
// path (caught, ceiling still wins), or losing the race to the React effect: this script simply does
// nothing further, and DesignSurface.tsx's own (slower, already proven, unmodified) boot effect is
// exactly as it was before this file existed — a pure addition, no removed guard, no new failure mode
// that was not already possible.

import {
  BOOT_DEPENDENCIES,
  DESIGN_HYDRATED_ATTR,
  DESIGN_SCRIPT_FAILED,
  DESIGN_SCRIPT_LOADED,
  DESIGN_SCRIPT_SRC_ATTR,
  DESIGN_SCRIPT_STATE_ATTR,
  EARLY_BOOT_LIVENESS_PROP,
  HYDRATION_GATED_DEPENDENCIES,
} from "./design-script-readiness";

// The three classic scripts, the marker attribute and the two completion-marker values are IMPORTED,
// not re-declared: IMPL-004 made the readiness rule a single shared contract
// (design-script-readiness.ts — constants only, so this server component stays off the client
// boundary), and a second hand-written copy of the attribute names inside this string is exactly how
// the two writers drifted apart in the first place.

/** How long to wait for the brand webfonts before revealing anyway. Sized off the actual files
 *  (apps/root/public/design-system/fonts/*.ttf, 140KB–350KB each): comfortably above what the handful
 *  of weights an above-the-fold viewport actually requests should take even on a throttled connection,
 *  short relative to the 10–14s this replaces. A bound, not a target — `Promise.race` below stops the
 *  moment fonts.ready resolves, so a fast/warm-cache load pays nothing for it. */
const FONT_CEILING_MS = 3500;

/** CORR-004 · MAP LOADING BOX. `<osm-map>` (osm-map.js) paints nothing until MapLibre has fetched, and
 *  its centre/zoom/fit are per-`data-key` runtime data (`window.SG_MAP_DATA[key]`, default zoom 10 with
 *  fit-to-pins). A static image of that view cannot be truthful without rendering it, and the only
 *  tiles shipped (`design/assets/map/tiles/z5-*`) are a coarse z5 prairie/Canada crop — an earlier draft
 *  tiled one of them and showed Saskatchewan under a Whistler map, which is exactly the fabrication
 *  this lane forbids. So this is the HONEST fallback the card permits: a neutral, geography-free box at
 *  the map's own final dimensions (the host is `position:absolute;inset:0`, so no layout shift), with
 *  no pins, no labels and no old map. It swaps out only once the real canvas AND every pin marker exist. */
const MAP_LOADING_BG = "#e9eef1";

/** Loader geometry: a 64px CSS ring spinner around the SnowGroup logomark (2.6KB SVG already shipped in
 *  design/assets/logos). Both sit inside the `inset:0` poster, so they cannot shift layout. */
const LOADER_RING_CSS =
  "position:absolute;width:64px;height:64px;border-radius:50%;box-sizing:border-box;border:3px solid rgba(114,171,184,0.28);border-top-color:#72abb8;";
const LOADER_MARK_CSS =
  "position:absolute;width:30px;height:33px;background:url(/design/assets/logos/snowgroup-logomark.svg) center/contain no-repeat;";

/** Bounded the same way the font wait is: a real map that is going to come up does so in well under
 *  this; a map that never will (offline MapLibre fetch, no matching `data-key`) leaves the poster in
 *  place forever rather than guessing it is done — fail-closed, same rule as the reveal below. */
const MAP_POSTER_TIMEOUT_MS = 8000;

/** The inline script. ES5 on purpose (matches ServerLayerParserFirst.tsx): it runs before any bundle,
 *  in whatever engine asked, and must not depend on anything this app's own JS has set up yet. */
export const EARLY_PAINT_REVEAL_SCRIPT =
  "(function(){" +
  "var host=document.currentScript&&document.currentScript.previousElementSibling;" +
  'if(!host||!host.getAttribute||host.getAttribute("data-sg-design-surface")===null)return;' +
  'if(!host.querySelector("[data-dc-tpl]"))return;' + // not SSR-painted (ssr=false) — the normal boot owns this host
  // IMPL-008 (run 2026-10-02) — LIVENESS, BEFORE ANY WAIT. From here on this script WILL attempt the
  // reveal, but `up` is lawfully held behind hydration (the osm-map gate below) and can lose
  // ServerLayerParserFirst's load+2000ms race on a slow transport — which used to uncover the legacy
  // fallback over this very host's already-visible SSR render ("no-boot-after-load" is a verdict
  // about a DEAD route chunk, and this chunk is parser-first and provably alive). The marker is NOT
  // a boot state: never "booting", never "up" (both stay behind the full dependency conjunction),
  // and the 20s stalled-boot reveal deliberately ignores it. See EARLY_BOOT_LIVENESS_PROP's header.
  `window.${EARLY_BOOT_LIVENESS_PROP}="waiting";` +
  // IMPL-008 — NOTHING MAY BE WRITTEN INTO AN `<osm-map>` BEFORE HYDRATION HAS COMMITTED. React renders
  // that element with no children; a poster (or MapLibre's container) already inside it when hydration
  // arrives is a server/client divergence and React discards the tree (#418). DesignSurface stamps the
  // host once its mount effect has run; the poster and the gated loader below wait for that stamp. No
  // ceiling on purpose: where hydration never commits nothing is ever revealed "up" either, which is the
  // fail-closed answer, and the server-rendered design is visible from first paint regardless (IMPL-003).
  "function whenHydrated(fn){" +
  `if(host.getAttribute("${DESIGN_HYDRATED_ATTR}")!==null){fn();return;}` +
  "var wo=new MutationObserver(function(){" +
  `if(host.getAttribute("${DESIGN_HYDRATED_ATTR}")!==null){wo.disconnect();fn();}});` +
  `wo.observe(host,{attributes:true,attributeFilter:["${DESIGN_HYDRATED_ATTR}"]});` +
  "}" +
  // MAP POSTER — independent of the boot-dependency reveal below (osm-map can come alive via the
  // slower React boot effect too), so it runs against every osm-map host the SERVER put in this surface —
  // the set is captured NOW (a static NodeList), and the poster is laid once hydration has committed
  // (above). Hosts React renders after hydration (hidden screens) are not in it, exactly as before.
  'var maps=host.querySelectorAll("osm-map");' +
  "whenHydrated(function(){" +
  "for(var mi=0;mi<maps.length;mi++){(function(mapEl){" +
  'if(mapEl.querySelector(".maplibregl-canvas"))return;' + // interactive world already up — never lay a poster over it
  'if(mapEl.querySelector("[data-sg-map-poster]"))return;' + // already posted (defensive re-run guard)
  'var poster=document.createElement("div");' +
  'poster.setAttribute("data-sg-map-poster","");' +
  'poster.setAttribute("aria-hidden","true");' +
  "poster.style.cssText=" + JSON.stringify("position:absolute;inset:0;background:" + MAP_LOADING_BG + ";display:flex;align-items:center;justify-content:center;") + ";" +
  // DESIGNED LOADER (founder-authorised): the SnowGroup logomark inside a spinning ring, centred in the
  // neutral box. Still geography-free — no tile, no pins, no labels. Animation uses the Web Animations
  // API so no stylesheet is injected, and is skipped under prefers-reduced-motion (static logomark).
  'var ring=document.createElement("div");' +
  "ring.style.cssText=" + JSON.stringify(LOADER_RING_CSS) + ";" +
  'var mark=document.createElement("div");' +
  "mark.style.cssText=" + JSON.stringify(LOADER_MARK_CSS) + ";" +
  "poster.appendChild(ring);poster.appendChild(mark);" +
  'if(ring.animate&&!(window.matchMedia&&window.matchMedia("(prefers-reduced-motion: reduce)").matches)){' +
  'ring.animate([{transform:"rotate(0deg)"},{transform:"rotate(360deg)"}],{duration:1100,iterations:Infinity});}' +
  "mapEl.insertBefore(poster,mapEl.firstChild);" +
  "function pinsReady(){" + // read off the SAME window.SG_MAP_DATA osm-map itself renders from — never a guess
  'var key=mapEl.getAttribute("data-key");' +
  "var data=key&&window.SG_MAP_DATA&&window.SG_MAP_DATA[key];" +
  "var pins=(data&&data.pins)||[];" +
  "var want=0;" +
  'for(var pi=0;pi<pins.length;pi++){if(typeof pins[pi].lat==="number"&&typeof pins[pi].lng==="number")want++;}' +
  "if(!want)return true;" + // this map genuinely has no pins to wait for
  'return mapEl.querySelectorAll(".maplibregl-marker").length>=want;' +
  "}" +
  "function tryRemove(){" +
  "if(!poster.isConnected)return true;" +
  'if(mapEl.querySelector(".maplibregl-canvas")&&pinsReady()){poster.remove();return true;}' +
  "return false;" +
  "}" +
  "if(tryRemove())return;" +
  "var obs=new MutationObserver(function(){if(tryRemove())obs.disconnect();});" +
  "obs.observe(mapEl,{childList:true,subtree:true});" +
  `setTimeout(function(){obs.disconnect();},${MAP_POSTER_TIMEOUT_MS});` +
  "})(maps[mi]);}" +
  "});" +
  `var urls=${JSON.stringify(BOOT_DEPENDENCIES)};` +
  "function loadOne(src){" +
  `var gated=${JSON.stringify(HYDRATION_GATED_DEPENDENCIES)}.indexOf(src)>=0;` + // osm-map.js: see whenHydrated
  "return new Promise(function(resolve){" +
  "function start(){" +
  `var existing=document.querySelector('script[${DESIGN_SCRIPT_SRC_ATTR}="'+src+'"]');` +
  // An existing tag only proves someone STARTED the fetch — it may be pending or already 404'd. Trust
  // the completion marker, else wait for the tag's own load/error. IMPL-004: the React boot effect's
  // loader (design-script-readiness.ts) now stamps the SAME marker from its own onload/onerror, so
  // there is no longer an unmarked-but-finished tag either writer can be fooled by; whichever writer
  // created the tag, the other reads a proven outcome or waits for the real event.
  `if(existing){var st=existing.getAttribute("${DESIGN_SCRIPT_STATE_ATTR}");` +
  `if(st==="${DESIGN_SCRIPT_LOADED}"){resolve(true);return;}if(st==="${DESIGN_SCRIPT_FAILED}"){resolve(false);return;}` +
  'existing.addEventListener("load",function(){resolve(true);});' +
  'existing.addEventListener("error",function(){resolve(false);});return;}' +
  'var s=document.createElement("script");' +
  `s.src=src;s.async=false;s.setAttribute("${DESIGN_SCRIPT_SRC_ATTR}",src);` +
  `s.onload=function(){s.setAttribute("${DESIGN_SCRIPT_STATE_ATTR}","${DESIGN_SCRIPT_LOADED}");resolve(true);};` +
  // CORR-004 FIX — a 404/network failure used to resolve() the same as onload, so a still-broken
  // boot dependency could still flip window.__SG_DESIGN_STATE to "up" below (a false "up": the
  // design system script never actually ran, but every later check that gates on designState==="up"
  // took the short-circuit as if it had). Failure now reports `false` and is checked below.
  `s.onerror=function(){s.setAttribute("${DESIGN_SCRIPT_STATE_ATTR}","${DESIGN_SCRIPT_FAILED}");resolve(false);};` +
  "document.head.appendChild(s);" +
  "}" +
  "if(gated)whenHydrated(start);else start();" +
  "});" +
  "}" +
  "var scripts=Promise.all(urls.map(loadOne));" +
  "var fonts=(document.fonts&&document.fonts.ready)?document.fonts.ready:Promise.resolve();" +
  `var ceiling=new Promise(function(resolve){setTimeout(resolve,${FONT_CEILING_MS});});` +
  "Promise.all([scripts,Promise.race([fonts,ceiling])]).then(function(results){" +
  "var loaded=results[0];" +
  // CORR-004 FIX — any failed (or, by construction of Promise.all, still-pending) boot dependency
  // must never set the design "up". Leave it exactly as before this script existed: the slower,
  // unmodified React boot effect re-verifies every dependency itself and owns recovery/fallback.
  "for(var ri=0;ri<loaded.length;ri++){if(!loaded[ri])return;}" +
  // Someone else — the React effect, or this same script on a different host — may already have
  // claimed the state. Never fight it, never reveal twice.
  "if(window.__SG_DESIGN_STATE!==undefined&&window.__SG_DESIGN_STATE!==null)return;" +
  "if(!host.isConnected)return;" +
  'if(!host.querySelector("[data-dc-tpl]"))return;' + // re-verify: nothing should have emptied it, but never assert stale state
  'host.style.visibility="visible";' +
  'host.dataset.sgDesign="up";' +
  'host.dataset.sgPaint="painted";' +
  'window.__SG_DESIGN_STATE="up";' +
  "});" +
  "})();";

/** Mount ONCE, immediately after the DesignSurface host div in document order — mirrors
 *  ServerLayerParserFirst's own placement contract. Server component: no "use client", no hooks. */
export default function EarlyPaintReveal() {
  return <script data-sg-early-paint-reveal="" dangerouslySetInnerHTML={{ __html: EARLY_PAINT_REVEAL_SCRIPT }} />;
}
